import type { FC, HTMLAttributes } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import parse from 'html-react-parser';

import { cn } from '@/lib/utils';

import DecoContent from '@/components/molecules/IconPointerList/components/DecoContent';
import type { ListItemType } from '@/components/molecules/IconPointerList/IconPointerList.types';

import InstallmentsModal from './components/InstallmentsModal';

const listItemVariants = cva('flex items-center', {
    variants: {
        variant: {
            separated:
                'gap-x-6 border-b border-b-gray-100 py-2 font-bold last:border-0 md:py-5 md:text-md',
            simple: 'mb-2 gap-x-4 text-xs last:mb-0',
        },
    },
    defaultVariants: {
        variant: 'simple',
    },
});

export interface IconPointerListType
    extends HTMLAttributes<HTMLUListElement>,
        VariantProps<typeof listItemVariants> {
    listData: ListItemType[];
}

const IconPointerList: FC<IconPointerListType> = ({
    listData,
    variant,
    className,
}) => (
    <ul className={className}>
        {listData.map(
            (
                {
                    Icon,
                    iconProps,
                    content,
                    prefixText,
                    decoContent,
                    showInstallmentsPlan,
                },
                i,
            ) => {
                const { title, subtitle, description } = content;

                return (
                    <li
                        key={'icons-pointer-list-item-' + i}
                        className={cn(
                            listItemVariants({ variant }),
                            showInstallmentsPlan && 'flex-wrap md:flex-nowrap',
                        )}
                    >
                        {!!Icon && (
                            <span
                                className={cn(
                                    'h-12 w-12 shrink-0 rounded-full bg-green-100 p-[9px] text-grey-900 lg:block',
                                    (decoContent || showInstallmentsPlan) &&
                                        'hidden',
                                )}
                            >
                                <Icon
                                    className="min-w-6 shrink-0 text-xl text-green-700"
                                    {...iconProps}
                                />
                            </span>
                        )}

                        <p className="flex-grow">
                            <span className="block text-left">
                                {parse(title)}
                            </span>

                            {subtitle && (
                                <span className="block text-2xs font-bold text-grey-700 sm:text-sm">
                                    {parse(subtitle)}
                                </span>
                            )}

                            {description && (
                                <span className="mt-2 block text-sm font-bold text-green-600 sm:mt-1 sm:text-sm">
                                    {parse(description)}
                                </span>
                            )}
                        </p>

                        {!!decoContent && (
                            <div
                                className={cn(
                                    'flex flex-col items-end justify-center gap-x-4',
                                    'md:flex-row md:items-center',
                                )}
                            >
                                {prefixText && (
                                    <span className="text-sm font-bold tracking-tight text-green-600 md:text-lg">
                                        {prefixText}
                                    </span>
                                )}

                                <DecoContent>{decoContent}</DecoContent>
                            </div>
                        )}

                        {!!showInstallmentsPlan && <InstallmentsModal />}
                    </li>
                );
            },
        )}
    </ul>
);

export default IconPointerList;
